import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ThumbsDown, ThumbsUp, MessageSquare } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import {
  knowledgeBaseApi,
  type KbFeedback,
  type KbQueryReviewRow,
} from "@/api/knowledgeBase";
import { useDateFormatter } from "@/utils/datetime";

// Knowledge Base v1.0 PR 5 — admin feedback review surface. Read-only,
// paginated, filter by feedback (NotHelpful surfaces corpus gaps) and
// answered/unanswered. The audit row's retrieved-citations snapshot is
// rendered inline so admins can spot the wrong document being cited.

type FeedbackFilter = "All" | KbFeedback;
type AnsweredFilter = "All" | "Answered" | "Unanswered";

const PAGE_SIZE = 25;

export function KnowledgeBaseQueriesAdminPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("KnowledgeBase.ManageDocuments");

  const [feedback, setFeedback] = useState<FeedbackFilter>("All");
  const [answered, setAnswered] = useState<AnsweredFilter>("All");
  const [page, setPage] = useState(1);

  const queryQ = useQuery({
    queryKey: ["kb", "admin", "queries", feedback, answered, page],
    queryFn: () =>
      knowledgeBaseApi.listAdminQueries({
        feedback: feedback === "All" ? undefined : feedback,
        wasAnswered:
          answered === "All" ? undefined : answered === "Answered",
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: canManage,
  });

  if (!canManage) {
    return (
      <div className="text-small text-status-error">
        You do not have permission to review knowledge base feedback.
      </div>
    );
  }

  const totalPages = Math.max(
    1,
    Math.ceil((queryQ.data?.totalCount ?? 0) / PAGE_SIZE),
  );

  return (
    <>
      <PageHeader
        eyebrow="Admin · Knowledge Base"
        title="Feedback review"
        description="Recent questions, the documents they cited, and the asker's thumbs up/down. Filter by Not helpful to surface corpus gaps and bad answers."
      />

      <div className="flex flex-wrap items-end gap-space-3 mb-space-4">
        <label className="flex flex-col gap-space-1">
          <span className="text-caption text-gray-mid">Feedback</span>
          <Select
            value={feedback}
            onChange={(e) => {
              setFeedback(e.target.value as FeedbackFilter);
              setPage(1);
            }}
            aria-label="Filter by feedback"
            className="md:w-44"
          >
            <option value="All">All</option>
            <option value="NotHelpful">Not helpful</option>
            <option value="Helpful">Helpful</option>
            <option value="None">No rating</option>
          </Select>
        </label>
        <label className="flex flex-col gap-space-1">
          <span className="text-caption text-gray-mid">Answered</span>
          <Select
            value={answered}
            onChange={(e) => {
              setAnswered(e.target.value as AnsweredFilter);
              setPage(1);
            }}
            aria-label="Filter by answered"
            className="md:w-44"
          >
            <option value="All">All</option>
            <option value="Answered">Answered</option>
            <option value="Unanswered">Unanswered</option>
          </Select>
        </label>
        <div className="ml-auto text-caption text-gray-mid">
          {queryQ.data
            ? `${queryQ.data.totalCount} result${queryQ.data.totalCount === 1 ? "" : "s"}`
            : queryQ.isLoading
              ? "Loading…"
              : "—"}
        </div>
      </div>

      {queryQ.isError ? (
        <div role="alert" className="text-small text-status-error">
          Could not load queries.{" "}
          {queryQ.error instanceof Error ? queryQ.error.message : ""}
        </div>
      ) : queryQ.isLoading ? (
        <div className="text-small text-gray-mid">Loading queries…</div>
      ) : (queryQ.data?.rows.length ?? 0) === 0 ? (
        <div className="rounded-md border border-border-light bg-white-warm p-space-5 text-center">
          <MessageSquare size={28} aria-hidden className="mx-auto text-gold-shadow" />
          <p className="text-small text-onyx mt-space-2">
            No questions match these filters.
          </p>
          <p className="text-caption text-gray-mid mt-space-1">
            Questions land here as users ask via the Knowledge Base.
          </p>
        </div>
      ) : (
        <ul className="space-y-space-3">
          {queryQ.data!.rows.map((row) => (
            <QueryRow key={row.id} row={row} />
          ))}
        </ul>
      )}

      {(queryQ.data?.totalCount ?? 0) > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-space-4">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <span className="text-caption text-gray-mid">
            Page {page} of {totalPages}
          </span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </>
  );
}

function QueryRow({ row }: { row: KbQueryReviewRow }) {
  const { dateTime } = useDateFormatter();
  return (
    <li className="rounded-md border border-border-light bg-white-warm p-space-4">
      <div className="flex items-start gap-space-3">
        <FeedbackBadge feedback={row.feedback} />
        <div className="min-w-0 flex-1">
          <div className="text-small font-display font-semibold text-onyx truncate">
            {row.questionText}
          </div>
          <div className="text-caption text-gray-mid mt-space-1">
            {row.wasAnswered ? "Answered" : "Not in corpus"}
            {" · "}
            {dateTime(row.createdAt)}
            {" · "}
            asker {row.userId}
            {row.modelUsed && ` · ${row.modelUsed}`}
            {row.latencyMs != null && ` · ${row.latencyMs}ms`}
          </div>
          <p className="text-small text-onyx mt-space-2 whitespace-pre-wrap">
            {row.answerText}
          </p>
          {row.citations.length > 0 && (
            <div className="mt-space-2">
              <div className="text-caption uppercase tracking-label text-gray-mid font-display font-semibold">
                Retrieved
              </div>
              <ul className="mt-space-1 flex flex-wrap gap-space-1">
                {row.citations.map((c) => (
                  <li
                    key={c.documentId}
                    className="inline-flex items-center gap-space-1 rounded border border-border-light px-space-2 py-px text-caption text-onyx"
                    title={
                      c.score != null
                        ? `score ${c.score.toFixed(3)}`
                        : undefined
                    }
                  >
                    <span className="truncate max-w-[16rem]">
                      {c.title ?? c.documentId}
                    </span>
                    {c.category && (
                      <span className="text-gray-mid">· {c.category}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function FeedbackBadge({ feedback }: { feedback: KbFeedback }) {
  if (feedback === "Helpful") {
    return (
      <span
        className="inline-flex shrink-0 items-center gap-space-1 rounded bg-status-success/15 px-space-2 py-px text-caption font-display font-semibold text-status-success"
        aria-label="Helpful"
      >
        <ThumbsUp size={12} aria-hidden /> Helpful
      </span>
    );
  }
  if (feedback === "NotHelpful") {
    return (
      <span
        className="inline-flex shrink-0 items-center gap-space-1 rounded bg-status-error/15 px-space-2 py-px text-caption font-display font-semibold text-status-error"
        aria-label="Not helpful"
      >
        <ThumbsDown size={12} aria-hidden /> Not helpful
      </span>
    );
  }
  return (
    <span
      className="inline-flex shrink-0 items-center rounded bg-border-light px-space-2 py-px text-caption font-display font-semibold text-gray-mid"
      aria-label="No rating"
    >
      No rating
    </span>
  );
}
